import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, fromEvent, Observable, Subscription } from 'rxjs';
import { IOrgPath, IResponsible, IUpdateResponsible, ResponsibleType } from '@shared/interfaces';
import { OrganisationService } from '@shared/services/organisation.service';
import { IQueryOrganisation } from '@shared/interfaces/queries/IQueryOrganisation';
import { OrgTreeService } from '@app/main/org-tree/services/org-tree.service';
import { OrgTreeActionsDirective } from '@app/main/org-tree/directives/org-tree-actions.directive';
import { TreeDatabase } from '@app/main/org-tree/services/tree.database';
import { DialogService, GuardService, SnackService } from '@shared/services';
import { convertQueryToParams } from '@shared/helpers/query-converter';
import { OrganisationConfigurationService } from '@shared/services/organisation-configuration.service';
import { ModuleService } from '@shared/services/module.service';
import { OrgModifyComponent } from './org-modify/org-modify.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-org-tree',
    templateUrl: './org-tree.component.html',
    styleUrls: ['./org-tree.component.scss'],
    providers: [TreeDatabase],
    standalone: false
})
export class OrgTreeComponent
    extends OrgTreeActionsDirective
    implements AfterContentChecked, OnInit, AfterViewInit, OnDestroy
{
    resizeObservable$: Observable<Event>;
    resizeSubscription$: Subscription;
    isWrapped: boolean;
    createDialogRef: MatDialogRef<OrgModifyComponent>;
    orgCodeInfo: string;
    subscriptions: Subscription[] = [];

    private readonly translateService = inject(TranslateService);

    constructor(
        private database: TreeDatabase,
        private cd: ChangeDetectorRef,
        private dialog: MatDialog,
        private route: ActivatedRoute,
        private organisationService: OrganisationService,
        private organisationConfigurationService: OrganisationConfigurationService,
        private router: Router,
        private dialogService: DialogService,
        public guardService: GuardService,
        private snackService: SnackService,
        orgTreeService: OrgTreeService,
        moduleService: ModuleService
    ) {
        super(orgTreeService, moduleService);
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.shouldReloadOrgTree.subscribe(() => this.loadData()),
            this.route.queryParamMap.subscribe(params => {
                const orgCode = params.get('orgCode') ?? null;
                const responsibleType = (params.get('responsibleType') as ResponsibleType) ?? null;
                this.orgCodeInfo = params.get('orgCodeInfo') ?? null;

                if (orgCode !== this.orgCode || responsibleType !== this.responsibleType) {
                    this.orgCode = orgCode;
                    this.responsibleType = responsibleType;
                    this.loadData();
                }

                this.router.navigateByUrl(
                    `/org-charts${this.getQueryParams(this.orgCode, this.responsibleType, this.orgCodeInfo)}`,
                    {
                        replaceUrl: true,
                        skipLocationChange: false
                    }
                );
            }),
            this.database.dataChange.subscribe(e => {
                this.rootNode = e;
                this.detectWrap();
                this.setIsWrapped();
            }),
            this.isEditActiveSubscription.subscribe(e => {
                this.detectWrap();
                this.setIsWrapped();
            }),
            this.toggleDrawerSubscription.subscribe(e => {
                if (!e?.isOpen && this.orgCodeInfo && !e?.isFirst) {
                    this.orgCodeInfo = null;
                    this.router.navigateByUrl(`/org-charts${this.getQueryParams(this.orgCode, this.responsibleType)}`, {
                        replaceUrl: true,
                        skipLocationChange: false
                    });
                }
            })
        );
    }

    ngAfterContentChecked(): void {
        this.cd.detectChanges();
    }

    ngAfterViewInit(): void {
        this.detectWrap();
        this.setIsWrapped();
        this.resizeObservable$ = fromEvent(window, 'resize');
        this.resizeSubscription$ = this.resizeObservable$.subscribe(evt => {
            this.detectWrap();
            this.setIsWrapped();
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(e => e.unsubscribe());
        this.orgTreeService.reset();
    }

    private async loadData() {
        const id = this.isOrgNode
            ? this.orgCode
            : this.responsibleType
              ? `responsibility-${this.responsibleType}`
              : null;

        const query: IQueryOrganisation = {
            orgCode: this.orgCode ?? null,
            responsibleType: this.responsibleType ? this.responsibleType : null
        };

        try {
            const [data, config] = await Promise.all([
                this.organisationService.getOrganisation(query),
                id && this.organisationConfigurationService.getConfigurationById(id)
            ]);

            this.currentConfiguration = config;
            this.database.data = data;
        } catch (e) {
            return this.router.navigateByUrl(`/org-charts`, {
                replaceUrl: true,
                skipLocationChange: false
            });
        }

        if (this.orgCodeInfo && !this.isDrawerOpen) {
            const activeItem = this.database.findOrgItemByCode(this.orgCodeInfo);
            if (activeItem) {
                this.toggleDrawer(activeItem);
            }
        }

        this.setIsWrapped();
    }

    override toggleDrawer(item?: IResponsible) {
        super.toggleDrawer(item);

        if (item) {
            setTimeout(() =>
                this.router.navigateByUrl(
                    `/org-charts${this.getQueryParams(this.orgCode, this.responsibleType, item.orgCode)}`,
                    {
                        replaceUrl: true,
                        skipLocationChange: false
                    }
                )
            );
        }
    }

    changeNodeByBreadcrumb(path: IOrgPath) {
        if (path.orgCode) {
            return this.changeNode(path.orgCode);
        }
        return this.router.navigateByUrl(`/org-charts${this.getQueryParams(null, path.responsibleType)}`, {
            replaceUrl: true
        });
    }

    changeNode(orgCode: string, responsibleType?: ResponsibleType): void {
        this.router.navigateByUrl(
            `/org-charts${this.getQueryParams(orgCode, orgCode ? null : responsibleType, this.isDrawerOpen && orgCode && !responsibleType ? this.rootNode.orgCode : null)}`,
            {
                replaceUrl: true,
                skipLocationChange: false
            }
        );

        if (this.isDrawerOpen) {
            this.toggleDrawer(!orgCode && !responsibleType ? null : this.rootNode);
        }
    }

    nodeClicked(item: IResponsible) {
        this.router.navigateByUrl(
            `/org-charts${this.getQueryParams(item.orgCode, item.orgCode ? null : item.responsibleType, this.isDrawerOpen ? item.orgCode : null)}`,
            {
                replaceUrl: true,
                skipLocationChange: false
            }
        );

        if (this.isDrawerOpen) {
            this.toggleDrawer(item.orgCode ? item : null);
        }
    }

    protected async nodeDeleted(item: IResponsible): Promise<void> {
        if (!item?.orgCode) {
            return;
        }

        const result = await firstValueFrom(
            this.dialogService.openDialog({
                title: this.translateService.instant('organisation.deleteOrganisationDialog.title', {
                    orgCode: item.orgCode
                }),
                message: this.translateService.instant('organisation.deleteOrganisationDialog.message', {
                    orgCode: item.orgCode
                }),
                sureQuestion: true
            })
        );
        if (!result) {
            return;
        }

        await this.organisationService.deleteOrganisation(item);
        this.loadData();
    }

    // A function to check if any wrap happened. Required for better visualization
    setIsWrapped(): void {
        if (this.rootNode?.children?.length) {
            const container = document.getElementById('list');

            this.isWrapped = container?.offsetWidth < 280 * (this.rootNode.children.length + 1);
        } else {
            // detect wrap isnt called
            this.isWrapped = false;
            setTimeout(() => {
                const element = document.getElementsByClassName('create-org-container')[0];
                element?.classList?.add('hideEndLine', 'hideFirstLine');
            });
        }
    }

    // A function to check if any wrap happened. The function adds/ removes the lines that connect organizations
    detectWrap(): void {
        let prevItem: DOMRect;
        let currItem: DOMRect;
        let lastShowLeftLine = 0;
        const items = document.getElementsByClassName('children');

        // remove previous styling
        for (const item of Array.from(items)) {
            if (item.classList.contains('create-org-container')) {
                item.className = 'children create-org-container';
            } else {
                item.className = 'children';
            }
        }

        // set last element of the previous line to not have right line
        // set first element of the new line to have left line
        for (let i = 0; i < items.length; i++) {
            currItem = items[i].getBoundingClientRect();
            if (prevItem && prevItem.top < currItem.top) {
                items[i].classList.add('showLeftLine');
                items[i - 1].classList.add('hideEndLine');
                lastShowLeftLine = i;
            }
            prevItem = currItem;
        }

        if (items.length > 0) {
            // set first element of this category to have left line
            items[0]?.classList?.add('showLeftLine');
        }

        // set last element of this category to not have right line
        items[items.length - 1]?.classList?.add('hideEndLine');
        items[lastShowLeftLine]?.classList?.remove('showLeftLine');

        if (lastShowLeftLine === 0) {
            items[lastShowLeftLine]?.classList?.add('hideFirstLine');
        }
    }

    openDialogModify(selectedItem?: IResponsible): void {
        this.createDialogRef = this.dialog.open(OrgModifyComponent, {
            panelClass: 'custom-dialog',
            width: '600px',
            data: {
                parentItem: this.rootNode,
                selectedItem
            }
        });

        this.createDialogRef.afterClosed().subscribe(async (data: IUpdateResponsible) => {
            if (data) {
                try {
                    const item = data?.id
                        ? await this.organisationService.updateItem(data)
                        : await this.organisationService.createItem(data);
                    if (!data?.id && this.freezeDateReached) {
                        const dialogResult = await this.dialogService
                            .openDialog({
                                title: 'Reporting period started',
                                message:
                                    'Reporting period has already started. Do you want to create a report for the current reporting period?',
                                sureQuestion: true
                            })
                            .toPromise();

                        if (dialogResult) {
                            await this.organisationService.startReportingPeriod({
                                ids: [item.id],
                                orgCode: item.parentOrgCode,
                                responsibleType: item.responsibleType,
                                allSelected: false
                            });
                        }
                    }

                    this.loadData();
                } catch (e) {
                    this.snackService.openSnackBarError(e.message);
                }
            }
        });
    }

    getQueryParams(orgCode?: string, responsibleType?: ResponsibleType, orgCodeInfo?: string): string {
        const query: IOrgTreeQueryParams = {
            orgCode: orgCode ? orgCode : null,
            responsibleType: !orgCode && responsibleType ? responsibleType : null,
            orgCodeInfo: orgCodeInfo ?? null
        };

        return convertQueryToParams<IOrgTreeQueryParams>(query);
    }

    protected async unPublishNode(item: IResponsible): Promise<void> {
        const result = await firstValueFrom(
            this.dialogService.openDialog({
                title: this.translateService.instant('organisation.unpublishDialog.title', {
                    orgCode: item.orgCode
                }),
                message: this.translateService.instant('organisation.unpublishDialog.message', {
                    orgCode: item.orgCode
                }),
                sureQuestion: true
            })
        );
        if (!result) {
            return;
        }

        try {
            await this.organisationService.unPublishReportForNode(item.id);
            this.loadData();
            super.toggleDrawer(item, true);
        } catch (e) {
            this.snackService.openSnackBarError(e.error.message);
        }
    }

    protected async resetNode(item: IResponsible): Promise<void> {
        const result = await firstValueFrom(
            this.dialogService.openDialog({
                title: this.translateService.instant('organisation.resetReportDialog.title', {
                    orgCode: item.orgCode
                }),
                message: this.translateService.instant('organisation.resetReportDialog.message', {
                    orgCode: item.orgCode
                }),
                sureQuestion: true
            })
        );
        if (!result) {
            return;
        }

        await this.organisationService.resetReportForNode(item.id);
        this.loadData();
    }
}

export interface IOrgTreeQueryParams {
    responsibleType?: ResponsibleType;
    orgCode?: string;
    orgCodeInfo?: string;
}
